const REQUIRED_PARAMETERS = Object.freeze([
  'prompt',
  'interaction_uid',
  'client_id',
  'client_name',
  'issuer',
  'scope',
  'csrf_token',
  'return_to'
])
const SUPPORTED_PROMPTS = new Set(['login', 'consent'])
const SUPPORTED_SCOPES = new Set([
  'openid',
  'offline_access',
  'mcp:read',
  'mcp:trade'
])
const IDENTIFIER_PATTERN = /^[A-Za-z0-9_-][A-Za-z0-9._:-]{1,159}$/
const CSRF_PATTERN = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/

function containsControlCharacters(value) {
  return [...value].some(character => {
    const code = character.codePointAt(0)
    return (
      code <= 31 ||
      (code >= 127 && code <= 159) ||
      (code >= 0x200b && code <= 0x200f) ||
      (code >= 0x202a && code <= 0x202e) ||
      (code >= 0x2060 && code <= 0x2069) ||
      code === 0xfeff
    )
  })
}

function invalidRequest() {
  return new Error('This connection request is invalid or has expired.')
}

function exactParameter(searchParams, name) {
  const values = searchParams.getAll(name)
  if (values.length !== 1 || values[0].trim() !== values[0]) {
    throw invalidRequest()
  }
  return values[0]
}

function isLoopback(hostname) {
  return ['localhost', '127.0.0.1', '[::1]', '::1'].includes(hostname)
}

function parseBoundUrl(value, { allowInsecureLocalhost }) {
  let url
  try {
    url = new URL(value)
  } catch {
    throw invalidRequest()
  }
  const secure = url.protocol === 'https:'
  const localDevelopment =
    allowInsecureLocalhost &&
    url.protocol === 'http:' &&
    isLoopback(url.hostname)
  if (
    (!secure && !localDevelopment) ||
    url.username ||
    url.password ||
    url.search ||
    url.hash ||
    url.href.length > 2048
  ) {
    throw invalidRequest()
  }
  return url
}

function parseScopes(value) {
  if (!value || value.length > 256) throw invalidRequest()
  const scopes = value.split(' ')
  if (
    scopes.some(scope => !scope || !SUPPORTED_SCOPES.has(scope)) ||
    new Set(scopes).size !== scopes.length
  ) {
    throw invalidRequest()
  }
  return scopes
}

export function parseX3ConnectAuthorization(
  search,
  { allowInsecureLocalhost = false } = {}
) {
  const searchParams = new URLSearchParams(search)
  const actualNames = [...new Set(searchParams.keys())].sort()
  const expectedNames = [...REQUIRED_PARAMETERS].sort()
  if (
    actualNames.length !== expectedNames.length ||
    actualNames.some((name, index) => name !== expectedNames[index])
  ) {
    throw invalidRequest()
  }

  const prompt = exactParameter(searchParams, 'prompt')
  const interactionUid = exactParameter(searchParams, 'interaction_uid')
  const clientId = exactParameter(searchParams, 'client_id')
  const clientName = exactParameter(searchParams, 'client_name')
  const issuer = exactParameter(searchParams, 'issuer')
  const scopes = parseScopes(exactParameter(searchParams, 'scope'))
  const csrfToken = exactParameter(searchParams, 'csrf_token')
  const returnTo = exactParameter(searchParams, 'return_to')

  if (
    !SUPPORTED_PROMPTS.has(prompt) ||
    !IDENTIFIER_PATTERN.test(interactionUid) ||
    !IDENTIFIER_PATTERN.test(clientId) ||
    !clientName ||
    clientName.length > 120 ||
    containsControlCharacters(clientName) ||
    csrfToken.length < 64 ||
    csrfToken.length > 2048 ||
    !CSRF_PATTERN.test(csrfToken)
  ) {
    throw invalidRequest()
  }

  const issuerUrl = parseBoundUrl(issuer, { allowInsecureLocalhost })
  if (issuerUrl.pathname !== '/' && issuerUrl.pathname.endsWith('/')) {
    throw invalidRequest()
  }
  const normalizedIssuer = issuerUrl.href.replace(/\/$/, '')
  const expectedReturnTo =
    `${normalizedIssuer}/interaction/${encodeURIComponent(interactionUid)}`
  const returnUrl = parseBoundUrl(returnTo, { allowInsecureLocalhost })
  if (returnUrl.href !== expectedReturnTo) throw invalidRequest()

  return Object.freeze({
    prompt,
    interactionUid,
    clientId,
    clientName,
    issuer: normalizedIssuer,
    scopes: Object.freeze(scopes),
    csrfToken,
    returnTo: returnUrl.href
  })
}

export function authenticationRedirect(loginUrl, currentLocation) {
  if (currentLocation?.pathname !== '/connect/authorize') return loginUrl
  const target = new URL(loginUrl, currentLocation.origin)
  target.searchParams.set('redirect', currentLocation.href)
  return target.href
}
